import { LoadingDots } from "./LoadingDots";
import { PlayIcon, AlertTriangleIcon } from "./Icons";
import { PrimaryButton } from "./Button";
import { SheetContainer } from "./SheetContainer";
import { strings } from "../strings";

interface StartSheetProps {
  greetingTitle?: string | null;
  greetingBody?: string | null;
  onStart: () => void;
  isLoading?: boolean;
  permissionState?: "granted" | "prompt" | "denied";
  permissionInstructions?: string;
  className?: string;
}

/**
 * Converts plain text with newlines into an array of paragraphs.
 */
function formatGreetingAsParagraphs(text: string): string[] {
  return text
    .split(/[\r\n]+/)
    .map((para) => para.trim())
    .filter((para) => para.length > 0);
}

export function StartSheet({
  greetingTitle,
  greetingBody,
  onStart,
  isLoading = false,
  permissionState = "prompt",
  permissionInstructions = strings.startSheet.defaultPermissionDenied,
  className,
}: StartSheetProps) {
  const displayTitle = greetingTitle || strings.startSheet.defaultTitle;
  const displayBody = greetingBody || strings.startSheet.defaultBody;
  const displayBodyParagraphs = formatGreetingAsParagraphs(displayBody);

  const isButtonDisabled = isLoading || permissionState === "denied";

  return (
    <SheetContainer className={`min-h-[400px] ${className || ""}`}>
      {/* Greeting section */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold mb-3 text-foreground">
          {displayTitle}
        </h2>
        <div className="text-muted-foreground flex flex-col gap-3">
          {displayBodyParagraphs.map((para, idx) => (
            <p key={idx} className="m-0 leading-relaxed">
              {para}
            </p>
          ))}
        </div>
      </div>

      {/* Separator */}
      <div className="h-px bg-border mb-6" />

      {/* Mic explainer section */}
      {permissionState !== "denied" ? (
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-3 text-foreground">
            {strings.startSheet.voiceChatTitle}
          </h3>
          <div className="text-muted-foreground flex flex-col gap-2">
            <p className="m-0 leading-relaxed">
              {strings.startSheet.voiceChatDescription}
            </p>
            <p className="m-0 leading-relaxed">
              {strings.startSheet.micPermissionHint}
            </p>
          </div>
        </div>
      ) : (
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-3 text-foreground">
            {strings.startSheet.voiceChatTitle}
          </h3>
          {/* Permission denied warning */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/15 border border-destructive/50">
            <AlertTriangleIcon
              width={16}
              height={16}
              className="text-destructive shrink-0 mt-0.5"
            />
            <p className="m-0 text-sm leading-relaxed text-destructive">
              {permissionInstructions}
            </p>
          </div>
        </div>
      )}

      {/* Start button */}
      <div>
        <PrimaryButton
          onClick={onStart}
          disabled={isButtonDisabled}
          className="w-full"
        >
          {isLoading ? (
            <>
              <LoadingDots size="sm" className="mr-2" />
              {strings.startSheet.buttonLoading}
            </>
          ) : permissionState === "prompt" ? (
            <>
              <PlayIcon width={20} height={20} className="mr-2" />
              {strings.startSheet.buttonEnableMic}
            </>
          ) : (
            <>
              <PlayIcon width={20} height={20} className="mr-2" />
              {strings.startSheet.buttonStart}
            </>
          )}
        </PrimaryButton>
      </div>
    </SheetContainer>
  );
}
